import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import axios from "axios";
import BASEURL from "../../pages/Utils/Baseurl";
import GoogleMapKey from "../../pages/Utils/googleMapKey";
import MaterialSelect from "../../components/MasterSelect";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import LocationOnRounded from "@mui/icons-material/LocationOnRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import FilterAltOffRounded from "@mui/icons-material/FilterAltOffRounded";
import FilterAltRounded from "@mui/icons-material/FilterAltRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AssignmentTurnedInRounded from "@mui/icons-material/AssignmentTurnedInRounded";
import EventAvailableRounded from "@mui/icons-material/EventAvailableRounded";
import InventoryRounded from "@mui/icons-material/InventoryRounded";
import TodayRounded from "@mui/icons-material/TodayRounded";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import ShoppingCartRounded from "@mui/icons-material/ShoppingCartRounded";
import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import LocalShippingRounded from "@mui/icons-material/LocalShippingRounded";
import ReportProblemRounded from "@mui/icons-material/ReportProblemRounded";
import AssignmentReturnRounded from "@mui/icons-material/AssignmentReturnRounded";
import request from "graphql-request";
import "./LocationMap.css";

export const DashboardCountsQuery = `
  query Dashboard {
    totalProductCount {
      success
      error
      message
      totalProductCount
    }
    todaysProductCount {
      success
      error
      message
      todaysProductCount
    }
    todaysVisitCustomerCount {
      success
      error
      message
      todaysVisitCustomerCount
    }
    todaysSalesOrderCount {
      success
      error
      message
      todaysSalesOrderCount
    }
    todaysPaymentBalanceCount {
      success
      error
      message
      todaysPaymentBalanceCount
    }
    unassignedDeliveriesCount {
      success
      error
      message
      unassignedDeliveriesCount
    }
    unresolvedComplaintsCount {
      success
      error
      message
      unresolvedComplaintsCount
    }
    pendingReturnsCount {
      success
      error
      message
      pendingReturnsCount
    }
  }
`;

const containerStyle = {
  width: "100%",
  height: "var(--location-map-height)",
};

/* ─── Card Decorative Full-Background SVG Graphics ───────── */
const BlueWaveGraphic = () => (
  <svg
    className="stat-graphic stat-graphic--wave"
    viewBox="0 0 300 140"
    fill="none"
    preserveAspectRatio="none"
  >
    <path
      d="M75,140 Q150,140 205,85 Q250,55 300,60 L300,140 Z"
      fill="url(#blue-bg-wave)"
    />
    <path
      d="M95,140 Q160,135 210,85 Q250,55 285,58 L300,56"
      stroke="#60a5fa"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="stat-anim-wave"
    />
    <circle
      cx="285"
      cy="58"
      r="3.5"
      fill="#2563eb"
      className="stat-accent-dot"
    />
    <defs>
      <linearGradient id="blue-bg-wave" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#bfdbfe" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#eff6ff" stopOpacity="0.08" />
      </linearGradient>
    </defs>
  </svg>
);

const VioletWaveGraphic = () => (
  <svg
    className="stat-graphic stat-graphic--glow-hill"
    viewBox="0 0 300 140"
    fill="none"
    preserveAspectRatio="none"
  >
    <path
      d="M80,140 Q150,140 200,90 Q245,45 300,60 L300,140 Z"
      fill="url(#violet-bg-hill)"
      className="stat-anim-float"
    />
    <path
      d="M110,140 Q175,125 220,78 Q265,48 300,66"
      stroke="#c4b5fd"
      strokeWidth="2"
      strokeDasharray="6 4"
      opacity="0.75"
    />
    <circle
      cx="275"
      cy="56"
      r="3.5"
      fill="#8b5cf6"
      className="stat-accent-dot"
    />
    <defs>
      <linearGradient id="violet-bg-hill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ddd6fe" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#f5f3ff" stopOpacity="0.08" />
      </linearGradient>
    </defs>
  </svg>
);

const TealBarChartGraphic = () => (
  <div className="stat-graphic-composite">
    <svg
      className="stat-graphic__wave"
      viewBox="0 0 300 140"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M50,140 Q140,140 200,95 Q250,55 300,65 L300,140 Z"
        fill="url(#teal-bg-wave)"
      />
      <defs>
        <linearGradient id="teal-bg-wave" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#99f6e4" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#f0fdfa" stopOpacity="0.08" />
        </linearGradient>
      </defs>
    </svg>
    <div className="stat-graphic__art stat-graphic__art--bars">
      <svg viewBox="0 0 120 80" fill="none">
        <rect
          x="6"
          y="48"
          width="7"
          height="32"
          rx="3.5"
          fill="#ccfbf1"
          className="stat-bar stat-bar--1"
        />
        <rect
          x="20"
          y="28"
          width="7"
          height="52"
          rx="3.5"
          fill="#99f6e4"
          className="stat-bar stat-bar--2"
        />
        <rect
          x="34"
          y="52"
          width="7"
          height="28"
          rx="3.5"
          fill="#ccfbf1"
          className="stat-bar stat-bar--3"
        />
        <rect
          x="48"
          y="18"
          width="7"
          height="62"
          rx="3.5"
          fill="#5eead4"
          className="stat-bar stat-bar--4"
        />
        <rect
          x="62"
          y="38"
          width="7"
          height="42"
          rx="3.5"
          fill="#99f6e4"
          className="stat-bar stat-bar--5"
        />
        <rect
          x="76"
          y="8"
          width="7"
          height="72"
          rx="3.5"
          fill="#14b8a6"
          className="stat-bar stat-bar--6"
        />
        <rect
          x="90"
          y="24"
          width="7"
          height="56"
          rx="3.5"
          fill="#2dd4bf"
          className="stat-bar stat-bar--7"
        />
        <rect
          x="104"
          y="36"
          width="7"
          height="44"
          rx="3.5"
          fill="#99f6e4"
          className="stat-bar stat-bar--8"
        />
      </svg>
    </div>
  </div>
);

const OrangeWaveGraphic = () => (
  <svg
    className="stat-graphic stat-graphic--orange-waves"
    viewBox="0 0 300 140"
    fill="none"
    preserveAspectRatio="none"
  >
    <path
      d="M30,140 Q130,140 190,85 Q240,45 300,55 L300,140 Z"
      fill="url(#orange-bg-1)"
      className="stat-anim-float"
    />
    <path
      d="M100,140 Q180,130 230,80 Q270,50 300,65 L300,140 Z"
      fill="url(#orange-bg-2)"
    />
    <path
      d="M70,140 Q150,125 210,75 Q250,45 300,62"
      stroke="#fb923c"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.65"
    />
    <defs>
      <linearGradient id="orange-bg-1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fed7aa" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#fff7ed" stopOpacity="0.08" />
      </linearGradient>
      <linearGradient id="orange-bg-2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fdba74" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0.05" />
      </linearGradient>
    </defs>
  </svg>
);

const GreenCoinGraphic = () => (
  <div className="stat-graphic-composite">
    <svg
      className="stat-graphic__wave"
      viewBox="0 0 300 140"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M20,140 Q120,135 180,85 Q240,45 300,55 L300,140 Z"
        fill="url(#green-bg-wave)"
      />
      <defs>
        <linearGradient id="green-bg-wave" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bbf7d0" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#f0fdf4" stopOpacity="0.08" />
        </linearGradient>
      </defs>
    </svg>
    <div className="stat-graphic__art stat-graphic__art--coin stat-anim-coin">
      <svg viewBox="0 0 44 44" fill="none">
        <circle
          cx="22"
          cy="22"
          r="20"
          fill="#ffffff"
          filter="drop-shadow(0 4px 10px rgba(22, 163, 74, 0.18))"
        />
        <circle
          cx="22"
          cy="22"
          r="19"
          stroke="#86efac"
          strokeWidth="1.6"
          fill="#f0fdf4"
        />
        <text
          x="22"
          y="26"
          textAnchor="middle"
          fill="#16a34a"
          fontSize="13"
          fontWeight="bold"
          fontFamily="system-ui, sans-serif"
        >
          BHD
        </text>
      </svg>
    </div>
  </div>
);

const RoseBoxGraphic = () => (
  <div className="stat-graphic-composite">
    <svg
      className="stat-graphic__wave"
      viewBox="0 0 300 140"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M20,140 Q120,135 180,85 Q240,45 300,55 L300,140 Z"
        fill="url(#rose-bg-wave)"
      />
      <defs>
        <linearGradient id="rose-bg-wave" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fecdd3" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#fff1f2" stopOpacity="0.08" />
        </linearGradient>
      </defs>
    </svg>
    <div className="stat-graphic__art stat-graphic__art--box stat-anim-float-soft">
      <svg viewBox="0 0 64 64" fill="none">
        <polygon points="32,6 58,19 32,32 6,19" fill="#fee2e2" />
        <polygon points="6,19 32,32 32,58 6,45" fill="#fca5a5" />
        <polygon
          points="32,32 58,19 58,45 32,58"
          fill="#f87171"
          opacity="0.9"
        />
        <polygon points="32,6 36,8 20,16 16,14" fill="#fff" opacity="0.65" />
        <polygon points="32,32 36,34 36,58 32,56" fill="#fff" opacity="0.5" />
      </svg>
    </div>
  </div>
);

const AmberShieldGraphic = () => (
  <div className="stat-graphic-composite">
    <svg
      className="stat-graphic__wave"
      viewBox="0 0 300 140"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M20,140 Q120,135 180,85 Q240,45 300,55 L300,140 Z"
        fill="url(#amber-bg-wave)"
      />
      <defs>
        <linearGradient id="amber-bg-wave" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fde68a" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#fffbeb" stopOpacity="0.08" />
        </linearGradient>
      </defs>
    </svg>
    <div className="stat-graphic__art stat-graphic__art--shield stat-anim-shield">
      <svg viewBox="0 0 56 60" fill="none">
        <path
          d="M28,4 C40,4 52,10 52,22 C52,40 28,56 28,56 C28,56 4,40 4,22 C4,10 16,4 28,4 Z"
          fill="url(#amber-shield-grad)"
          stroke="#fcd34d"
          strokeWidth="2.5"
        />
        <rect
          x="25.75"
          y="17"
          width="4.5"
          height="17"
          rx="2.25"
          fill="#d97706"
        />
        <circle cx="28" cy="40" r="2.8" fill="#d97706" />
        <defs>
          <linearGradient id="amber-shield-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#fffbeb" stopOpacity="0.45" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
);

const IndigoReturnGraphic = () => (
  <div className="stat-graphic-composite">
    <svg
      className="stat-graphic__wave"
      viewBox="0 0 300 140"
      fill="none"
      preserveAspectRatio="none"
    >
      <path
        d="M20,140 Q120,135 180,85 Q240,45 300,55 L300,140 Z"
        fill="url(#indigo-bg-wave)"
      />
      <defs>
        <linearGradient id="indigo-bg-wave" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c7d2fe" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#f3f4ff" stopOpacity="0.08" />
        </linearGradient>
      </defs>
    </svg>
    <div className="stat-graphic__art stat-graphic__art--return stat-anim-float-soft">
      <svg viewBox="0 0 60 60" fill="none">
        <polygon points="30,12 54,24 28,36 4,24" fill="#e0e7ff" />
        <polygon points="4,24 28,36 28,58 4,46" fill="#a5b4fc" />
        <polygon points="28,36 54,24 54,46 28,58" fill="#818cf8" />
        <path
          d="M48,8 C35,-1 22,2 17,10"
          stroke="#6366f1"
          strokeWidth="3.5"
          strokeLinecap="round"
          fill="none"
          className="stat-anim-arrow"
        />
        <polygon points="13,6 17,15 9,12" fill="#6366f1" />
      </svg>
    </div>
  </div>
);

/* ─── Stat Card ─────────────────────────────────────────── */
const StatCard = ({
  icon: Icon,
  label,
  value,
  accent,
  loading,
  route,
  graphic,
  onClick,
}) => (
  <div
    className={`stat-card stat-card--${accent}`}
    onClick={onClick}
    style={{ cursor: route || onClick ? "pointer" : "default" }}
    role={onClick || route ? "button" : undefined}
    tabIndex={onClick || route ? 0 : undefined}
    onKeyDown={(e) => {
      if ((e.key === "Enter" || e.key === " ") && onClick) {
        e.preventDefault();
        onClick();
      }
    }}
  >
    {/* Full-card SVG Background */}
    <div className="stat-card__bg-graphic">{graphic}</div>

    {/* Content (z-index above SVG background) */}
    <div className="stat-card__content">
      {/* Icon and count in the SAME ROW */}
      <div className="stat-card__header-row">
        <div className="stat-card__icon">
          <Icon style={{ fontSize: 21 }} />
        </div>
        <div className="stat-card__value-wrap">
          {loading ? (
            <span className="stat-card__shimmer" />
          ) : (
            <strong className="stat-card__value">{value ?? "0"}</strong>
          )}
        </div>
      </div>

      {/* Label below the row */}
      <span className="stat-card__label">{label}</span>
    </div>

    {/* Bottom gradient accent line */}
    <div className="stat-card__bottom-bar" />
  </div>
);

/* ─── Custom High-Visibility Employee Map Marker ─────────── */
const markerIconCache = new Map();

const escapeXml = (str) =>
  String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const createEmployeeMarkerIcon = (name) => {
  if (!window.google) return null;
  const rawName = name || "Employee";
  const displayName =
    rawName.length > 20 ? rawName.slice(0, 18) + "…" : rawName;
  const escapedName = escapeXml(displayName);

  // Calculate width based on text length
  const textLen = displayName.length;
  const pillWidth = Math.max(76, Math.round(textLen * 7.4 + 28));
  const totalWidth = pillWidth + 16;
  const totalHeight = 58;
  const pinX = Math.round(totalWidth / 2);
  const pillX = Math.round((totalWidth - pillWidth) / 2);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">
    <defs>
      <filter id="pill-shadow-${pinX}" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#0f172a" flood-opacity="0.18" />
      </filter>
      <filter id="pin-shadow-${pinX}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#15803d" flood-opacity="0.28" />
      </filter>
      <linearGradient id="pin-grad-${pinX}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#22c55e" />
        <stop offset="100%" stop-color="#15803d" />
      </linearGradient>
    </defs>

    <!-- Floating Name Pill -->
    <g filter="url(#pill-shadow-${pinX})">
      <rect x="${pillX}" y="2" width="${pillWidth}" height="24" rx="12" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.2" />
      <circle cx="${pillX + 11}" cy="14" r="3.5" fill="#16a34a" />
      <circle cx="${pillX + 11}" cy="14" r="5.5" fill="#86efac" opacity="0.45" />
      <text x="${pillX + 21}" y="17.5" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" font-size="11" font-weight="700" fill="#0f172a">${escapedName}</text>
    </g>

    <!-- Location Pin Stem & Head -->
    <g filter="url(#pin-shadow-${pinX})">
      <path d="M ${pinX - 6} 38 L ${pinX} 53 L ${pinX + 6} 38 Z" fill="#15803d" />
      <circle cx="${pinX}" cy="38" r="8.5" fill="url(#pin-grad-${pinX})" stroke="#ffffff" stroke-width="2" />
      <circle cx="${pinX}" cy="38" r="3" fill="#ffffff" />
    </g>
  </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(totalWidth, totalHeight),
    anchor: new window.google.maps.Point(pinX, 53),
  };
};

const getEmployeeMarkerIcon = (name) => {
  if (!window.google) return null;
  const key = name || "Employee";
  if (markerIconCache.has(key)) {
    return markerIconCache.get(key);
  }
  const icon = createEmployeeMarkerIcon(key);
  markerIconCache.set(key, icon);
  return icon;
};

/* ─── Main Component ─────────────────────────────────────── */
const CameraMap = () => {
  const navigate = useNavigate();

  /* --- Map state ----------------------------------------- */
  const [lists, setLists] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [mapCenter] = useState({ lat: 26.0667, lng: 50.5577 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* --- Filter dropdowns ---------------------------------- */
  const [department, setDepartment] = useState([]);
  const [loadingDepartment, setLoadingDepartment] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  /* --- Dashboard stat cards ------------------------------ */
  const [stats, setStats] = useState({
    totalProducts: null,
    todayProducts: null,
    visitingCustomers: null,
    todaySalesOrders: null,
    todayPaymentBalance: null,
    undeliveredCount: null,
    pendingComplaints: null,
    pendingReturns: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  /* ─── Fetch dashboard statistics ──────────────────────── */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await request(BASEURL, DashboardCountsQuery);

      setStats({
        totalProducts: res?.totalProductCount?.totalProductCount ?? 0,
        todayProducts: res?.todaysProductCount?.todaysProductCount ?? 0,
        visitingCustomers:
          res?.todaysVisitCustomerCount?.todaysVisitCustomerCount ?? 0,
        todaySalesOrders:
          res?.todaysSalesOrderCount?.todaysSalesOrderCount ?? 0,
        todayPaymentBalance:
          res?.todaysPaymentBalanceCount?.todaysPaymentBalanceCount ?? 0,
        undeliveredCount:
          res?.unassignedDeliveriesCount?.unassignedDeliveriesCount ?? 0,
        pendingComplaints:
          res?.unresolvedComplaintsCount?.unresolvedComplaintsCount ?? 0,
        pendingReturns: res?.pendingReturnsCount?.pendingReturnsCount ?? 0,
      });
    } catch (e) {
      console.error("Failed to fetch dashboard statistics", e);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  /* ─── Map helpers ──────────────────────────────────────── */
  const fetchCoordinates = useCallback(async (departmentId, employeeId) => {
    const query = `
      query LiveLocation($departmentId: Int, $employeeId: Int) {
        liveLocation(departmentId: $departmentId, employeeId: $employeeId) {
          employee {
            id
            employeeName
            department {
              id
              departmentName
            }
          }
          checkIn{
            checkInTime
            checkInDate
            checkOuts{
              checkOutTime
            }
          }
          latitude
          longitude
        }
      }
    `;

    try {
      setLoading(true);
      setError(null);
      setSelectedShop(null);
      const result = await axios.post(BASEURL, {
        query,
        variables: { departmentId, employeeId },
      });

      const mappedLocations = (result?.data?.data?.liveLocation || []).map(
        (location) => ({
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
          employeeName: location?.employee?.employeeName || "N/A",
          departmentName:
            location?.employee?.department?.departmentName || "Field Staff",
          checkInTime: location?.checkIn?.checkInTime || "N/A",
          checkOutTime:
            location?.checkIn?.checkOuts?.[0]?.checkOutTime || "N/A",
        }),
      );

      setLists(mappedLocations);
    } catch (err) {
      setError("Failed to load employee locations.");
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepartment = useCallback(async () => {
    const query = `
      query{
  allDepartmentsDropdown{
    data{
      id
      departmentName
    }
    error
    success
    message
  }
}
    `;

    try {
      setLoadingDepartment(true);
      const result = await axios.post(BASEURL, { query });

      const departmentData =
        await result?.data?.data?.allDepartmentsDropdown?.data?.map((dept) => ({
          id: dept?.id || "N/A",
          departmentName: dept?.departmentName || "N/A",
        }));
      setDepartment(departmentData);
      setLoadingDepartment(false);
    } catch (err) {
      setError("Failed to load employee locations.");
      setDepartment([]);
      setLoadingDepartment(false);
    }
  }, []);

  const fetchEmployees = useCallback(async (departmentId, signal) => {
    const query = `
      query employeeDropdownByDepartment($departmentId: ID!) {
        employeeDropdownByDepartment(departmentId: $departmentId) {
          success
          error
          message
          data {
            id
            employeeName
            employeeCode
            email
          }
        }
      }
    `;

    try {
      setLoadingEmployees(true);
      const result = await axios.post(
        BASEURL,
        { query, variables: { departmentId } },
        { signal },
      );

      const employeeData = (
        result?.data?.data?.employeeDropdownByDepartment?.data || []
      ).map((employee) => {
        const employeeName = employee?.employeeName || "N/A";
        const employeeCode = employee?.employeeCode;

        return {
          id: employee?.id || "N/A",
          employeeName:
            employeeCode && !employeeName.includes(`(${employeeCode})`)
              ? `${employeeName} (${employeeCode})`
              : employeeName,
        };
      });

      setEmployees(employeeData);
    } catch (err) {
      if (!signal.aborted) setEmployees([]);
    } finally {
      if (!signal.aborted) setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartment();
  }, [fetchDepartment]);

  useEffect(() => {
    if (!selectedDepartment?.value) {
      setEmployees([]);
      setLoadingEmployees(false);
      return undefined;
    }

    const controller = new AbortController();
    setEmployees([]);
    fetchEmployees(selectedDepartment.value, controller.signal);

    return () => controller.abort();
  }, [fetchEmployees, selectedDepartment]);

  useEffect(() => {
    const previousAuthFailure = window.gm_authFailure;
    const handleMapsAuthFailure = () => {
      setError(
        "Google Maps rejected this API key. Check its restrictions, billing, and Maps JavaScript API access.",
      );
    };

    window.gm_authFailure = handleMapsAuthFailure;

    return () => {
      if (window.gm_authFailure !== handleMapsAuthFailure) return;

      if (previousAuthFailure) {
        window.gm_authFailure = previousAuthFailure;
      } else {
        delete window.gm_authFailure;
      }
    };
  }, []);

  useEffect(() => {
    fetchCoordinates(
      selectedDepartment ? Number(selectedDepartment.value) : null,
      selectedEmployee ? Number(selectedEmployee.value) : null,
    );
  }, [fetchCoordinates, selectedDepartment, selectedEmployee]);

  const mapRef = useRef(null);

  const onLoad = (map) => {
    mapRef.current = map;

    if (lists.length > 0 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      lists.forEach((location) =>
        bounds.extend({ lat: location.lat, lng: location.lng }),
      );
      map.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (!mapRef.current || !window.google || lists.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    lists.forEach((location) =>
      bounds.extend({ lat: location.lat, lng: location.lng }),
    );
    mapRef.current.fitBounds(bounds);
  }, [lists]);

  /* ─── Stat cards config ─────────────────────────────────── */
  const statCards = [
    {
      icon: AssignmentTurnedInRounded,
      label: "Total Products",
      value: stats.totalProducts,
      accent: "blue",
      route: "/product-masters",
      graphic: <BlueWaveGraphic />,
    },
    {
      icon: EventAvailableRounded,
      label: "Today's Products",
      value: stats.todayProducts,
      accent: "violet",
      route: "/todays-products",
      graphic: <VioletWaveGraphic />,
    },
    {
      icon: PeopleAltRounded,
      label: "Visiting Customers",
      value: stats.visitingCustomers,
      accent: "teal",
      route: "/visit-customer",
      graphic: <TealBarChartGraphic />,
    },
    {
      icon: ShoppingCartRounded,
      label: "Today Sales Orders",
      value: stats.todaySalesOrders,
      accent: "orange",
      route: "/sales-order",
      graphic: <OrangeWaveGraphic />,
    },
    {
      icon: AccountBalanceWalletRounded,
      label: "Payment Balance",
      value: stats.todayPaymentBalance,
      accent: "green",
      route: "/paymentBalance",
      graphic: <GreenCoinGraphic />,
    },
    {
      icon: LocalShippingRounded,
      label: "Unassigned Deliveries",
      value: stats.undeliveredCount,
      accent: "rose",
      route: "/Manage-Deliveries",
      graphic: <RoseBoxGraphic />,
    },
    {
      icon: ReportProblemRounded,
      label: "Pending Complaints",
      value: stats.pendingComplaints,
      accent: "amber",
      route: "/manage-complaints",
      graphic: <AmberShieldGraphic />,
    },
    {
      icon: AssignmentReturnRounded,
      label: "Pending Returns",
      value: stats.pendingReturns,
      accent: "indigo",
      route: "/returns-management",
      graphic: <IndigoReturnGraphic />,
    },
  ];

  const hasFilters = selectedDepartment || selectedEmployee;

  return (
    <main className="location-page">
      {/* ── Header ─────────────────────────────────────── */}
      <header className="location-page__header">
        <div className="location-page__heading">
          <h1>Dashboard</h1>
          <p>Live overview of your operations at a glance.</p>
        </div>
        <div className="location-page__actions">
          <div className="location-page__count" aria-live="polite">
            <span className="location-page__count-icon">
              <LocationOnRounded fontSize="small" />
            </span>
            <span>
              <strong>{lists.length}</strong>
              <small>{lists.length === 1 ? "location" : "locations"}</small>
            </span>
          </div>
        </div>
      </header>

      {/* ── Stat Cards Grid ────────────────────────────── */}
      <section className="stat-cards-grid" aria-label="Dashboard statistics">
        {statCards.map((card) => (
          <StatCard
            key={card.label}
            {...card}
            loading={statsLoading}
            onClick={() => card.route && navigate(card.route)}
          />
        ))}
      </section>

      {/* ── Map Panel ──────────────────────────────────── */}
      <section
        className="location-map-panel"
        aria-label="Employee location map"
      >
        <div className="location-map-panel__bar">
          <div className="location-map-panel__title">
            <span
              className={`location-status-dot${error ? " is-error" : ""}`}
            />
            <strong>{error ? "Map unavailable" : "Location map"}</strong>
            <span className="location-map-panel__divider" />
            <span className="location-map-panel__detail">
              {loading
                ? "Updating locations"
                : error
                  ? "Connection issue"
                  : "Latest check-ins"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <button
              className="location-filter-btn"
              type="button"
              onClick={() => setFilterOpen(true)}
              aria-label="Open filters"
              title="Filter map"
            >
              <FilterAltRounded fontSize="small" />
              <span>Filters</span>
              {hasFilters && <span className="location-filter-badge" />}
            </button>
            {hasFilters && (
              <button
                className="location-clear-btn"
                type="button"
                onClick={() => {
                  setSelectedDepartment(null);
                  setSelectedEmployee(null);
                }}
                aria-label="Clear map filters"
                title="Clear filters"
              >
                <FilterAltOffRounded fontSize="small" />
                <span>Clear</span>
              </button>
            )}
            <button
              className="location-refresh"
              type="button"
              onClick={() => {
                fetchCoordinates(
                  selectedDepartment ? Number(selectedDepartment.value) : null,
                  selectedEmployee ? Number(selectedEmployee.value) : null,
                );
                fetchStats();
              }}
              disabled={loading || statsLoading}
              aria-label="Refresh employee locations and dashboard statistics"
              title="Refresh"
            >
              <RefreshRounded fontSize="small" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="location-map-canvas">
          {error ? (
            <div
              className="location-map-state location-map-state--error"
              role="alert"
            >
              <span className="location-map-state__icon">
                <ErrorOutlineRounded />
              </span>
              <h2>We couldn't load locations</h2>
              <p>{error} Check your connection and try again.</p>
              <button
                className="location-retry"
                type="button"
                onClick={() =>
                  fetchCoordinates(
                    selectedDepartment
                      ? Number(selectedDepartment.value)
                      : null,
                    selectedEmployee ? Number(selectedEmployee.value) : null,
                  )
                }
              >
                <RefreshRounded fontSize="small" />
                Try again
              </button>
            </div>
          ) : (
            <div
              style={{ width: "100%", height: "100%" }}
              aria-label="Google Map"
            >
              <LoadScript
                googleMapsApiKey={GoogleMapKey}
                onError={() =>
                  setError(
                    "Google Maps could not load. Verify the API key, billing, and allowed referrers.",
                  )
                }
              >
                <GoogleMap
                  onLoad={onLoad}
                  mapContainerStyle={containerStyle}
                  center={mapCenter}
                  zoom={10}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                    clickableIcons: false,
                  }}
                >
                  {lists.map((location, index) => {
                    const icon = getEmployeeMarkerIcon(location.employeeName);
                    return (
                      <Marker
                        key={`${location.employeeName}-${index}`}
                        position={{ lat: location.lat, lng: location.lng }}
                        onClick={() => setSelectedShop(location)}
                        icon={icon || undefined}
                        title={location.employeeName}
                      />
                    );
                  })}

                  {selectedShop && (
                    <InfoWindow
                      position={{
                        lat: selectedShop.lat,
                        lng: selectedShop.lng,
                      }}
                      onCloseClick={() => setSelectedShop(null)}
                      options={{
                        pixelOffset: window.google
                          ? new window.google.maps.Size(0, -42)
                          : undefined,
                      }}
                    >
                      <div className="location-info-window">
                        <div className="location-info-window__header">
                          <div className="location-info-window__avatar">
                            {selectedShop.employeeName?.charAt(0) || "E"}
                          </div>
                          <div className="location-info-window__meta">
                            <strong className="location-info-window__name">
                              {selectedShop.employeeName}
                            </strong>
                            <span className="location-info-window__dept">
                              {selectedShop.departmentName || "Field Staff"}
                            </span>
                          </div>
                        </div>

                        <div className="location-info-window__body">
                          <div className="location-info-window__row">
                            <span className="location-info-window__label">
                              Check-in
                            </span>
                            <span className="location-info-window__value location-info-window__value--in">
                              {selectedShop.checkInTime || "N/A"}
                            </span>
                          </div>
                          <div className="location-info-window__row">
                            <span className="location-info-window__label">
                              Check-out
                            </span>
                            <span className="location-info-window__value location-info-window__value--out">
                              {selectedShop.checkOutTime || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </LoadScript>
            </div>
          )}

          {!error && loading && (
            <div className="location-map-loading" role="status">
              <span className="location-map-loading__spinner" />
              <span>Updating team locations...</span>
            </div>
          )}

          {!error && !loading && lists.length === 0 && (
            <div className="location-map-state location-map-state--empty">
              <span className="location-map-state__icon">
                <LocationOnRounded />
              </span>
              <h2>No locations to show</h2>
              <p>There are no reported positions for the selected filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Filter Top Modal ──────────────────────── */}
      {filterOpen && (
        <div
          className="filter-dialog-overlay"
          onClick={() => setFilterOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Map filters"
        >
          <div className="filter-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="filter-dialog__header">
              <div className="filter-dialog__title">
                <FilterAltRounded fontSize="small" />
                <strong>Map Filters</strong>
              </div>
              <button
                className="filter-dialog__close"
                type="button"
                onClick={() => setFilterOpen(false)}
                aria-label="Close filters"
              >
                <CloseRounded fontSize="small" />
              </button>
            </div>

            <div className="filter-dialog__body">
              <div className="filter-dialog__row">
                <label className="location-filter">
                  <span>Department</span>
                  <MaterialSelect
                    size="small"
                    name="department"
                    menuPlacement="auto"
                    value={selectedDepartment}
                    options={department.map((item) => ({
                      value: item.id,
                      label: item.departmentName,
                    }))}
                    onChange={(option) => {
                      setSelectedDepartment(option);
                      setSelectedEmployee(null);
                    }}
                    isLoading={loadingDepartment}
                    isClearable
                    placeholder="All departments"
                  />
                </label>
                <label className="location-filter">
                  <span>Employee</span>
                  <MaterialSelect
                    size="small"
                    name="employee"
                    menuPlacement="auto"
                    value={selectedEmployee}
                    options={employees.map((employee) => ({
                      value: employee.id,
                      label: employee.employeeName,
                    }))}
                    onChange={setSelectedEmployee}
                    isLoading={loadingEmployees}
                    isDisabled={!employees.length || loadingEmployees}
                    isClearable
                    placeholder="All employees"
                  />
                </label>
              </div>
            </div>

            <div className="filter-dialog__footer">
              <button
                className="filter-dialog__clear"
                type="button"
                onClick={() => {
                  setSelectedDepartment(null);
                  setSelectedEmployee(null);
                }}
                disabled={!selectedDepartment && !selectedEmployee}
              >
                <FilterAltOffRounded fontSize="small" />
                Clear filters
              </button>
              <button
                className="filter-dialog__apply"
                type="button"
                onClick={() => setFilterOpen(false)}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default CameraMap;
